import { useState } from "react";
import { FileTypeIcon } from "./file-type-icon";
import type { DocumentFile } from "@/types/documents";
import { cx } from "@/utils/cx";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { ExtractionStatusBadge } from "./extraction-status-badge";
import { ValidationStatusBadge } from "./validation-status-badge";
import { VersionIndicator } from "./version-indicator";
import { Mail01, Upload01, Code01, Zap, DotsVertical } from "@untitledui/icons";

/**
 * Format upload source for display
 */
const formatUploadSource = (source?: string) => {
    switch (source) {
        case "email":
            return { label: "Email", icon: Mail01 };
        case "manual":
            return { label: "Manual", icon: Upload01 };
        case "api":
            return { label: "API", icon: Code01 };
        case "agent":
            return { label: "Agent", icon: Zap };
        default:
            return { label: source || "Unknown", icon: Upload01 };
    }
};

/**
 * Format date for display (dd/mm/yyyy HH:MM)
 */
const formatDate = (date: Date | string | undefined) => {
    if (!date) return "-";
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, "0");
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const year = d.getFullYear();
    const hours = d.getHours().toString().padStart(2, "0");
    const minutes = d.getMinutes().toString().padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}`;
};

interface DocumentCardProps {
    document: DocumentFile;
    onClick?: () => void;
    onContextMenu?: (e: React.MouseEvent) => void;
    isSelected?: boolean;
    viewMode?: "card" | "list";
    className?: string;
    onCheckboxChange?: (checked: boolean) => void;
    showCheckbox?: boolean;
    /** Callback quando si seleziona una versione dal dropdown */
    onVersionSelect?: (documentId: string, version: number) => void;
}

export const DocumentCard = ({
    document,
    onClick,
    onContextMenu,
    isSelected = false,
    viewMode = "card",
    className,
    onCheckboxChange,
    showCheckbox = false,
    onVersionSelect,
}: DocumentCardProps) => {
    const [isHovered, setIsHovered] = useState(false);
    const shouldShowCheckbox = showCheckbox && (isHovered || isSelected);

    if (viewMode === "list") {
        const uploadSource = formatUploadSource(document.uploadSource);
        const UploadSourceIcon = uploadSource.icon;

        return (
            <div
                className={cx(
                    "flex items-center gap-3 rounded-lg px-4 py-3 cursor-pointer transition-all relative",
                    "hover:bg-primary_hover focus-visible:outline-2 focus-visible:outline-offset-2",
                    isSelected && "bg-active ring-2 ring-brand",
                    className
                )}
                onClick={onClick}
                onContextMenu={onContextMenu}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                role="button"
                tabIndex={0}
            >
                {shouldShowCheckbox && onCheckboxChange && (
                    <div
                        className="absolute left-2 top-1/2 -translate-y-1/2 z-10"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Checkbox
                            isSelected={isSelected}
                            onChange={(checked) => onCheckboxChange(checked)}
                        />
                    </div>
                )}
                {/* File Icon + Name */}
                <FileTypeIcon type={document.type} fileName={document.name} mimeType={document.mimeType} size="md" />
                <div className="flex-1 min-w-0 min-w-[200px]">
                    <p className="text-sm font-medium text-primary truncate">{document.name}</p>
                </div>

                {/* Document Type (Category) */}
                <div className="w-[120px] shrink-0">
                    <span className="text-xs text-secondary capitalize px-2 py-1 bg-secondary rounded-md">
                        {document.typeName || document.type.replace(/_/g, " ")}
                    </span>
                </div>

                {/* Extraction Status */}
                <div className="w-[100px] shrink-0">
                    {document.extractionStatus && (
                        <ExtractionStatusBadge status={document.extractionStatus} size="sm" />
                    )}
                </div>

                {/* Validation Status */}
                <div className="w-[100px] shrink-0">
                    <ValidationStatusBadge status={document.validationStatus} size="sm" />
                </div>

                {/* Version Indicator */}
                <div className="w-[90px] shrink-0" onClick={(e) => e.stopPropagation()}>
                    <VersionIndicator
                        documentId={document.id}
                        versionCount={document.versionCount}
                        currentVersion={document.version}
                        onVersionSelect={onVersionSelect}
                        size="sm"
                    />
                </div>

                {/* Upload Method */}
                <div className="w-[80px] shrink-0 flex items-center gap-1">
                    <UploadSourceIcon className="w-3.5 h-3.5 text-tertiary" />
                    <span className="text-xs text-tertiary">{uploadSource.label}</span>
                </div>

                {/* Upload Time */}
                <div className="w-[120px] shrink-0 text-xs text-tertiary">
                    {formatDate(document.uploadedAt)}
                </div>

                {/* Last Modified */}
                <div className="w-[120px] shrink-0 text-xs text-tertiary">
                    {formatDate(document.updatedAt)}
                </div>

                {/* Actions Button (More Options) */}
                <div className="w-[40px] shrink-0 flex justify-end">
                    {(isHovered || isSelected) && onContextMenu && (
                        <button
                            className="p-1.5 rounded-md hover:bg-secondary transition-colors"
                            onClick={(e) => {
                                e.stopPropagation();
                                onContextMenu(e);
                            }}
                            title="More actions"
                        >
                            <DotsVertical className="w-4 h-4 text-tertiary" />
                        </button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div
            className={cx(
                "rounded-xl bg-primary p-4 shadow-xs ring-1 ring-secondary cursor-pointer transition-all relative",
                "hover:ring-2 hover:shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2",
                isSelected && "ring-2 ring-brand shadow-md",
                className
            )}
            onClick={onClick}
            onContextMenu={onContextMenu}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            role="button"
            tabIndex={0}
        >
            {shouldShowCheckbox && onCheckboxChange && (
                <div
                    className="absolute top-2 left-2 z-10"
                    onClick={(e) => e.stopPropagation()}
                >
                    <Checkbox
                        isSelected={isSelected}
                        onChange={(checked) => onCheckboxChange(checked)}
                    />
                </div>
            )}
            {/* Actions Button (More Options) - Card View */}
            {(isHovered || isSelected) && onContextMenu && (
                <button
                    className="absolute top-2 right-2 p-1.5 rounded-md hover:bg-secondary transition-colors z-10"
                    onClick={(e) => {
                        e.stopPropagation();
                        onContextMenu(e);
                    }}
                    title="More actions"
                >
                    <DotsVertical className="w-4 h-4 text-tertiary" />
                </button>
            )}
            <div className="flex flex-col items-center text-center gap-3">
                <FileTypeIcon type={document.type} fileName={document.name} mimeType={document.mimeType} size="lg" className="text-brand-600" />
                <div className="w-full min-w-0">
                    <p className="text-sm font-medium text-primary truncate" title={document.name}>
                        {document.name}
                    </p>
                    <p className="text-xs text-tertiary mt-1 capitalize">
                        {document.typeName || document.type.replace(/_/g, " ")}
                    </p>
                    {document.extractionStatus && (
                        <div className="mt-2">
                            <ExtractionStatusBadge status={document.extractionStatus} size="sm" />
                        </div>
                    )}
                    {/* Version Indicator - Card View */}
                    <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                        <VersionIndicator
                            documentId={document.id}
                            versionCount={document.versionCount}
                            currentVersion={document.version}
                            onVersionSelect={onVersionSelect}
                            size="sm"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

